<template>
  <div :id="`wrapperAll_${index}`" class="card-item">
    <!-- Drag handle -->
    <v-tooltip left color="surface_neutral_selected">
      <template v-slot:activator="{ on, attrs }">
        <div class="card-item__drag" role="button" tabindex="0" aria-label="Arraste para mover este item" v-bind="attrs" v-on="on">
          <v-icon size="18" color="text_secondary">mdi-drag</v-icon>
        </div>
      </template>
      <span>Segure e arraste para a coluna de exibição</span>
    </v-tooltip>

    <!-- Avatar -->
    <div class="card-item__avatar">
      <v-avatar
        size="48"
        style="cursor: pointer"
        @click="showModalFotoPerfil(item.url_avatar_colaborador)"
      >
        <v-img
          v-if="item.flg_sem_avatar === 'N' && !avatarError"
          :src="item.url_avatar_colaborador"
          alt="Avatar"
          @error="avatarError = true"
        />
        <v-img v-else src="@/assets/User.png" alt="Avatar" />
      </v-avatar>
    </div>

    <!-- Media (image/video/audio thumbnail) -->
    <div
      v-if="item.url_midia_colaboracao && item.dsc_tipo_midia_colaboracao !== 'audio'"
      class="card-item__media"
      @click="showModalImagem(item.url_midia_colaboracao, item.dsc_tipo_midia_colaboracao)"
    >
      <template v-if="item.dsc_tipo_midia_colaboracao === 'image'">
        <img
          v-if="!mediaError"
          :src="item.url_midia_colaboracao"
          alt="Imagem colaboração"
          style="width: 100%; height: 120px; object-fit: cover; border-radius: var(--sc-radius-md)"
          @error="mediaError = true"
        />
        <div v-else class="card-item__media-fallback">
          <v-icon size="32" color="text_secondary">mdi-image-off-outline</v-icon>
        </div>
      </template>
      <template v-if="item.dsc_tipo_midia_colaboracao === 'video'">
        <div style="position: relative; width: 100%">
          <v-img
            v-if="!thumbnailError"
            :src="item.url_midia_thumbnail"
            alt="Thumbnail"
            height="120"
            style="border-radius: var(--sc-radius-md)"
            @error="thumbnailError = true"
          >
            <div class="card-item__video-overlay">
              <v-icon size="32">mdi-play-circle-outline</v-icon>
            </div>
            <div v-if="isVideoMetadataLoaded" class="card-item__video-duration">
              {{ timerVideo }}
            </div>
            <div v-else class="card-item__video-duration">
              Em processamento
            </div>
          </v-img>
          <div v-else class="card-item__media-fallback">
            <v-icon size="32" color="text_secondary">mdi-video-off-outline</v-icon>
          </div>
        </div>
      </template>
    </div>

    <!-- Body -->
    <div class="card-item__body">
      <!-- Name + verified -->
      <div class="d-flex align-center" style="gap: 6px">
        <span class="card-item__title">{{ item.dsc_nome_colaborador }}</span>
        <v-icon
          v-if="item.flg_colaborador_verificado === 1"
          size="16"
          color="#009DF7"
        >
          mdi-check-decagram
        </v-icon>
      </div>

      <!-- Subtitle (user handle / UF / phone / email / complemento) -->
      <div v-if="hasSubtitleContent" class="card-item__subtitle">
        <a
          v-if="item.dsc_colaborador_user"
          target="_blank"
          rel="noopener"
          :href="userProfileUrl"
        >
          {{ item.dsc_colaborador_user }}
        </a>
        <template v-else>
          <span v-if="item.dsc_uf_colaborador">{{ item.dsc_uf_colaborador }}</span>
          <template v-if="item.dsc_uf_colaborador && (item.dsc_colaborador_phone || item.dsc_colaborador_email || item.dsc_complemento)">
            <span class="meta-separator"> · </span>
          </template>
          <span v-if="item.dsc_colaborador_phone">{{ formatCellPhone(item.dsc_colaborador_phone) }}</span>
          <template v-if="item.dsc_colaborador_phone && (item.dsc_colaborador_email || item.dsc_complemento)">
            <span class="meta-separator"> · </span>
          </template>
          <span v-if="item.dsc_colaborador_email" @click="copyEmailToClipboard" style="cursor: pointer">
            {{ item.dsc_colaborador_email }}
          </span>
          <template v-if="item.dsc_colaborador_email && item.dsc_complemento">
            <span class="meta-separator"> · </span>
          </template>
          <span v-if="item.dsc_complemento" class="card-item__info-inline">
            {{ item.dsc_complemento }}
          </span>
        </template>
      </div>

      <!-- Text content -->
      <p
        v-if="Boolean(item.flg_exibir_texto) && item.dsc_texto_colaboracao && item.dsc_texto_colaboracao.length > 0"
        class="card-item__info"
      >
        {{ item.dsc_texto_colaboracao }}
      </p>

      <!-- Audio player -->
      <CardAudioPlayer
        v-if="item.url_midia_colaboracao && item.dsc_tipo_midia_colaboracao === 'audio'"
        :key="item.url_midia_colaboracao"
        :src="item.url_midia_colaboracao"
        flush
      />

      <!-- Meta (source + date) -->
      <div class="card-item__meta">
        <span class="meta-source">{{ capitalize(item.dsc_fonte_colaboracao) }}</span>
        <span class="meta-separator">·</span>
        <span>{{ formatDate(item.dat_colaboracao) }}</span>
        <span class="meta-separator">·</span>
        <span>{{ formatHour(item.dat_colaboracao) }}</span>
      </div>
    </div>

    <!-- Actions -->
    <div class="card-item__actions">
      <v-tooltip left color="surface_neutral_selected">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="card-action-box card-action-box--move"
            v-bind="attrs"
            v-on="on"
            @click="adicionaColaboracao(item)"
           aria-label="Enviar para coluna de exibição">
            <v-icon>mdi-arrow-right</v-icon>
          </button>
        </template>
        <span>Enviar para coluna de exibição</span>
      </v-tooltip>

      <ModalExcluirColaboracao :colaboracao="item" v-if="!cod_pasta" />
      <ModalRemoverDaPasta :colaboracao="item" v-if="cod_pasta" />

      <ModalEditarColaboracao
        :key="`${item.id_colaboracao}-${item.cod_producao}`"
        :colaboracaoData="item"
        :num_coluna_telao="num_coluna_telao"
        tipoColaboracao="aprovadas"
      />

      <v-tooltip left color="surface_neutral_selected">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="card-action-box card-action-box--favorite"
            :class="{ 'is-bursting': bursting }"
            :aria-label="item.flg_favorita === 'N' ? 'Favoritar colaboração' : 'Desfavoritar colaboração'"
            v-bind="attrs"
            v-on="on"
            @click.prevent="handleFavoritarClick"
          >
            <v-icon v-if="item.flg_favorita === 'N' && !bursting">mdi-heart-outline</v-icon>
            <v-icon v-else>mdi-heart</v-icon>
            <span v-for="i in 5" :key="`hp-${i}`" :class="`heart-particle heart-particle--${i}`" aria-hidden="true">♥</span>
          </button>
        </template>
        <span>{{ item.flg_favorita === "N" ? "Favoritar colaboração" : "Desfavoritar colaboração" }}</span>
      </v-tooltip>

      <RecursosCardColaboracaoAprovada
        :item="item"
        :get-colaboracoes-aprovadas="getColaboracoesAprovadas"
        :page="currentPage"
        :flg_favorita="flg_favorita"
        :flg_exibida="flg_exibida"
        :cod_pasta="cod_pasta"
      />
    </div>

    <!-- Modals -->
    <ModalExibirFotoPerfil
      :dialog="isModalExibirFotoPerfilVisible"
      :urlFotoPerfil="profile_photo"
      @close="closeModalFotoPerfil"
    />
    <ModalImagem
      :dialog="isModalImagemAprovadasVisible"
      @close="closeModalImagem"
      :midia_url="midia_url_aprovadas"
      :media_type="media_type"
    />
  </div>
</template>

<script>
import ModalImagem from "@/components/Modal/ModalImagem.vue";
import ModalEditarColaboracao from "@/components/Modal/Colaboracao/ModalEditarColaboracao.vue";
import ModalExcluirColaboracao from "@/components/Modal/Colaboracao/ModalExcluirColaboracao.vue";
import ModalExibirFotoPerfil from "../Modal/ModalExibirFotoPerfil.vue";
import CardAudioPlayer from "@/components/Cards/CardAudioPlayer.vue";
import { apiClient } from "@/services/api";
import { capitalizeString } from "@/utils/capitalizeString";
import { formatDate, formatHour, formatCellPhone } from "@/utils/dateUtils";
import { mapActions, mapGetters } from "vuex";
import RecursosCardColaboracaoAprovada from "../Colaboracoes/RecursosCardColaboracaoAprovada.vue";
import ModalRemoverDaPasta from "@/components/Modal/Colaboracao/ModalRemoverDaPasta.vue";

export default {
  props: {
    item: Object,
    index: Number,
    getColaboracoesAprovadas: Function,
    getColaboracoesModeradas: Function,
    currentPage: Number,
    num_coluna_telao: Number,
  },
  components: {
    ModalEditarColaboracao,
    ModalExcluirColaboracao,
    ModalImagem,
    ModalExibirFotoPerfil,
    RecursosCardColaboracaoAprovada,
    ModalRemoverDaPasta,
    CardAudioPlayer,
  },
  data() {
    return {
      isModalImagemAprovadasVisible: false,
      isModalExibirFotoPerfilVisible: false,
      profile_photo: null,
      midia_url_aprovadas: "",
      media_type: "",
      timerVideo: null,
      isVideoMetadataLoaded: false,
      showColaboradorPhone: false,
      avatarError: false,
      mediaError: false,
      thumbnailError: false,
      bursting: false,
    };
  },
  created() {
    this.flg_favorita =
      JSON.parse(localStorage.getItem("@socialcam/flg_favorita")) || false;
    this.flg_exibida =
      JSON.parse(localStorage.getItem("@socialcam/flg_exibida")) || false;
  },
  mounted() {
    if (
      this.item.url_midia_colaboracao &&
      this.item.dsc_tipo_midia_colaboracao === "video"
    ) {
      const wrapperDiv = document.getElementById(`wrapperAll_${this.index}`);
      const videoElement = document.createElement("video");
      videoElement.setAttribute("src", this.item.url_midia_colaboracao);
      videoElement.setAttribute("id", `video_${this.index}`);
      videoElement.style.display = "none";
      wrapperDiv.appendChild(videoElement);
      videoElement.onloadedmetadata = () => {
        const duration = videoElement.duration;
        this.timerVideo = new Date(duration * 1000).toISOString().substring(14, 19);
        this.isVideoMetadataLoaded = true;
      };
    }
  },
  computed: {
    ...mapGetters({
      cod_pasta: "pastas/getterCodPasta",
    }),
    userProfileUrl() {
      const user = this.item.dsc_colaborador_user;
      const source = this.item.dsc_fonte_colaboracao;
      if (source === "twitter") return `//${`twitter.com/${user}`}`;
      if (source === "threads") return `//${`threads.net/${user}?hl=pt-br`}`;
      if (source === "bluesky") return `//${`bsky.app/profile/${user.replace("@", "")}`}`;
      return "";
    },
    hasSubtitleContent() {
      return !!(this.item.dsc_colaborador_user || this.item.dsc_uf_colaborador || this.item.dsc_colaborador_phone || this.item.dsc_colaborador_email || this.item.dsc_complemento);
    },
    subtitleText() {
      if (this.showColaboradorPhone && this.item.dsc_colaborador_phone) {
        return formatCellPhone(this.item.dsc_colaborador_phone);
      }
      if (this.item.dsc_uf_colaborador) {
        return this.item.dsc_uf_colaborador;
      }
      if (this.item.dsc_colaborador_email) {
        return this.item.dsc_colaborador_email;
      }
      return null;
    },
  },
  methods: {
    ...mapActions({
      incrementTotalItemsFavoritas: "colaboracoes_aprovadas/incrementTotalItemsFavoritas",
      setCopyColaboracoesRecebidas: "colaboracoes_aprovadas/setCopyColaboracoesRecebidas",
      setPageModerada: "colaboracoes_moderadas/setPageModerada",
    }),
    formatDate,
    formatHour,
    formatCellPhone,
    capitalize(string) {
      return capitalizeString(string);
    },
    copyEmailToClipboard() {
      try {
        navigator.clipboard.writeText(this.item.dsc_colaborador_email);
        this.$toast.info("Email copiado para a área de transferência");
      } catch (error) {
        console.error(error);
        this.$toast.error("Erro ao tentar copiar email");
      }
    },
    handleSubtitleClick() {
      this.showColaboradorPhone = !this.showColaboradorPhone;
    },
    showModalImagem(midia_url_aprovadas, media_type) {
      this.isModalImagemAprovadasVisible = true;
      this.midia_url_aprovadas = midia_url_aprovadas;
      this.media_type = media_type;
    },
    showModalFotoPerfil(url_avatar_colaborador) {
      this.isModalExibirFotoPerfilVisible = true;
      this.profile_photo = url_avatar_colaborador;
    },
    closeModalImagem() {
      this.isModalImagemAprovadasVisible = false;
    },
    closeModalFotoPerfil() {
      this.isModalExibirFotoPerfilVisible = false;
    },
    startDrag(evt, item) {
      evt.dataTransfer.dropEffect = "move";
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    async adicionaColaboracao(item) {
      const flg_exibida =
        JSON.parse(localStorage.getItem("@socialcam/flg_exibida")) || false;
      const colunaModeradaAtual = this.num_coluna_telao;

      const formData = {
        id_colaboracao: item.id_colaboracao,
        cod_producao: item.cod_producao,
        num_coluna_telao: colunaModeradaAtual,
        dat_moderacao: new Date(),
        dsc_login_moderacao: this.$store.state.users.user.dsc_login_usuario,
      };

      const response = await apiClient.post("/inserirColaboracaoModerada", formData);
      if (response.status === 200) {
        await this.getColaboracoesAprovadas({
          page: this.currentPage,
          size: 5,
          cod_producao: this.item.cod_producao,
          flg_favorita:
            JSON.parse(localStorage.getItem("@socialcam/flg_favorita")) || false,
          flg_exibida,
          cod_pasta: this.cod_pasta,
        });
        await this.getColaboracoesModeradas({
          num_coluna_telao: colunaModeradaAtual,
          page: 1,
          size: 5,
          cod_producao: this.item.cod_producao,
          filtro_texto_moderada: "",
        });
        this.$toast.success("Colaboração movida com sucesso.");
      }
      this.setCopyColaboracoesRecebidas(
        this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_recebidas
      );
      this.setPageModerada(1);
    },

    handleFavoritarClick() {
      // Heart-burst flourish only when un-favorited → favorited.
      if (this.item.flg_favorita === "N") {
        this.bursting = true;
        setTimeout(() => { this.bursting = false; }, 900);
      }
      this.favoritarColaboracao(this.item.id_colaboracao, this.item.flg_favorita, this.item.cod_producao);
    },

    async favoritarColaboracao(id, flg_favorita, cod_producao) {
      try {
        const flag = JSON.parse(localStorage.getItem("@socialcam/flg_favorita")) || false;
        const flg_exibida =
          JSON.parse(localStorage.getItem("@socialcam/flg_exibida")) || false;
        const response = await apiClient.patch(`/favoritarColaboracao/${id}`, {
          flg_favorita: flg_favorita === "S" ? "N" : "S",
          cod_producao,
        });

        if (response.status == 200) {
          await this.getColaboracoesAprovadas({
            page: this.currentPage,
            size: 5,
            cod_producao,
            flg_favorita: flag,
            flg_exibida,
            cod_pasta: this.cod_pasta,
          });

          await this.incrementTotalItemsFavoritas({
            flg_favorita: flg_favorita === "S" ? false : true,
          });
          this.setCopyColaboracoesRecebidas(
            this.$store.state.colaboracoes_aprovadas.colaboracoes.colaboracoes_recebidas
          );
        }
      } catch (error) {
        console.error(error);
        this.$toast.error("Não foi possível favoritar");
      }
    },
  },
};
</script>
